<template>
  <b-container fluid>
    <h1>Tournament Setup</h1>
    <div class="mb-4">
      <h2>Settings</h2>
      <b-row class="font-weight-bold">
        <b-col>Bracket</b-col>
        <b-col>Rounds</b-col>
        <b-col>Time</b-col>
        <b-col>Break</b-col>
        <b-col>Additional Time</b-col>
        <b-col>Total</b-col>
      </b-row>
      <b-row v-for="s in settings" :key="s.id" class="mb-2">
        <b-col>{{ s.label }}</b-col>
        <b-col>
          <div class="d-flex">
            <b-button @click="setRounds(s.id, -1)">-</b-button>
            <input
              class="form-control"
              disabled
              :value="s.rounds"
            />
            <b-button @click="setRounds(s.id, 1)">+</b-button>
          </div>
        </b-col>
        <b-col>
          <div class="d-flex">
            <b-form-select
              :options="possibleDurations"
              v-model="s.duration"
              @change="setDuration(s.id, s.duration)"
            />
          </div>
        </b-col>
        <b-col>
          <b-input value="1:00" disabled/>
        </b-col>
        <b-col>
          <div class="d-flex">
            <b-button @click="setAdditionalTime(s.id, -60)">-</b-button>
            <input
              class="form-control"
              disabled
              :value="$formattedTime(s.additionalTime)"
            />
            <b-button @click="setAdditionalTime(s.id, 60)">+</b-button>
          </div>
        </b-col>
        <b-col>
          <b-input disabled :value="totalTime(s.id)"/>
        </b-col>
      </b-row>
    </div> <!-- Settings -->
    <div class="mb-4">
      <h2>Matches</h2>
      <b-tabs>
        <b-tab
          v-for="wc in weightClasses"
          :key="wc.id"
          :title="getWeightClassDetail(wc.weight).stringDe"
          @click="setTab(wc.id)"
        >
          <b-table
            :fields="fields"
            head-variant="dark"
            :items="fights"
            small
          >
            <template v-slot:cell(bracket)="data">{{ getBracketName(data.item) }}</template>
            <template v-slot:cell(athleteRed)="data">
              <AthleteCell
                :fight="data.item"
                :athlete="data.item.athleteRed"
                corner="red"
              />
            </template>
            <template v-slot:cell(athleteBlue)="data">
              <AthleteCell
                :fight="data.item"
                :athlete="data.item.athleteBlue"
                corner="blue"
              />
            </template>
          </b-table>
          <div v-if="unassignedAthletes.length">
            <h4>Unassigned Athletes</h4>
            <b-table
              head-variant="dark"
              :items="unassignedAthletes"
              small
            />
          </div>
        </b-tab>
      </b-tabs>
    </div> <!-- Matches -->
    <div class="d-flex justify-content-between">
      <b-button
        :disabled="!everyoneAssigned"
        @click="$emit('back')"
      >Back to Officials</b-button>
      <b-button
        :disabled="!everyoneAssigned"
        variant="primary"
        @click="$emit('next')"
      >Go to Matchfights</b-button>
    </div>
  </b-container>
</template>

<script>
import '../../plugins/functionsTournamentSetup';
import AthleteCell from '@/components/Setup/AthleteCell.vue';

export default {
  name: 'SetupTournament',
  components: { AthleteCell },
  data() {
    return {
      fields: ['bracket', 'nr', 'athleteRed', 'athleteBlue'],
      fieldsUnassignedAthletes: ['name', 'team'],
      lastFightMockupId: 1,
      possibleDurations: [
        { value: 120, text: '02:00' },
        { value: 180, text: '03:00' },
        { value: 300, text: '05:00' },
      ],
    };
  },
  methods: {
    createFights(classId, athletes) {
      const PARAMS = {
        rules: 'amateur',
        rounds: 2,
        duration: 180,
        extraTime: 300,
        classId,
        eventId: null,
      };
      if (athletes.length <= 1) return;
      for (let i = athletes.length - 1; i > 0; i -= 1) {
        const blueCorner = athletes.length ? athletes.pop() : null;
        const redCorner = athletes.length ? athletes.pop() : null;
        this.createFightMockup(i, redCorner, blueCorner, PARAMS);
      }
    },
    createFightMockup(fightNumber, athleteRed, athleteBlue, params) {
      this.$store.commit('createFightMockup', {
        id: this.lastFightMockupId,
        schedule: null,
        startTime: null,
        nr: fightNumber,
        rules: params.rules,
        rounds: params.rounds,
        duration: params.duration,
        extraTime: params.extraTime,
        athleteRed,
        athleteBlue,
        eventId: params.eventId,
        classId: params.classId,
      });
      this.lastFightMockupId += 1;
    },
    getBracketName(fight) {
      if (fight.nr === 1) return 'Finals';
      if (fight.nr === 2) return 'Semi-Finals 1';
      if (fight.nr === 3) return 'Semi-Finals 2';
      if (fight.nr === 4) return 'Quarter-Finals 1';
      if (fight.nr === 5) return 'Quarter-Finals 2';
      if (fight.nr === 6) return 'Quarter-Finals 3';
      if (fight.nr === 7) return 'Quarter-Finals 4';
      return 'Quarter-Finals';
    },
    getWeightClassDetail(weight) {
      return this.$weightClasses().find((wc) => wc.weight === weight);
    },
    setAdditionalTime(id, value) {
      const item = this.$store.state.setup.settings.find((r) => r.id === id);
      let newValue = item.additionalTime + value;
      if (newValue < 0) newValue = 0;
      this.$store.commit('setTournamentSettings', {
        id,
        item: 'additionalTime',
        value: newValue,
      });
    },
    setDuration(id, value) {
      this.$store.commit('setTournamentSettings', {
        id,
        item: 'duration',
        value,
      });
    },
    setRounds(id, value) {
      const item = this.$store.state.setup.settings.find((r) => r.id === id);
      if (item.rounds + value >= 1 && item.rounds + value <= 5) {
        this.$store.commit('setTournamentSettings', {
          id,
          item: 'rounds',
          value: item.rounds + value,
        });
      }
    },
    setTab(id) {
      this.$store.commit('setCurrentWeightClass', id);
    },
    totalTime(id) {
      const item = this.$store.state.setup.settings.find((r) => r.id === id);
      const time = item.rounds * item.duration + (item.rounds - 1) * 60 + item.additionalTime;
      return this.$formattedTime(time);
    },
  },
  computed: {
    everyoneAssigned() {
      return this.$store.state.setup.classes.every((clazz) => clazz.athletes.length === 0);
    },
    fights() {
      return this.$store.getters.getFightMockups;
    },
    weightClass() {
      return this.$store.state.setup.classes
        .find((c) => c.id === this.$store.state.setup.currentWeightClass);
    },
    settings() {
      return this.$store.state.setup.settings;
    },
    unassignedAthletes() {
      return this.weightClass.athletes;
    },
    weightClasses() {
      return this.$store.state.setup.classes.map((clazz) => ({
        id: clazz.id,
        weight: clazz.weight,
      }));
    },
  },
  created() {
    // let data  = this.$http.get('classes?expand=registrations,athletes');
    this.$store.state.setup.classes.forEach((clazz) => {
      this.createFights(clazz.id, clazz.athletes);
    });
    this.$store.commit('setCurrentWeightClass', this.weightClasses[0].id);
  },
};
</script>

<style scoped>

</style>
